import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Loader2, Check, Play, Pause, Download, RotateCcw,
    AlertTriangle, Mic, Edit3, Volume2, RefreshCw
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { voiceover } from '../services/api';
import { trackAudioEvent } from '../utils/telemetry';

/**
 * VoiceoverModal — Script review/edit + audio generation + playback/download.
 *
 * Steps:
 *  1. Script Review — edit generated narration, regenerate, or generate audio
 *  2. Generating — phased loading state during audio generation
 *  3. Ready — audio player, download, regenerate
 */

const SCRIPT_PHASES = [
    'Reading your lesson plan…',
    'Writing your narration script…',
];

const AUDIO_PHASES = [
    'Sending script to your voice profile…',
    'Generating audio with your teaching voice…',
    'Almost done — processing audio…',
];

// Map known backend errors to teacher-friendly messages
const friendlyError = (err) => {
    const detail = err?.response?.data?.detail || err?.response?.data?.message || '';
    const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);

    if (msg.includes('No voice profile found') || msg.includes('voice first'))
        return 'You need to set up your voice first. This only takes a minute.';
    if (msg.includes('Audio generation failed') || msg.includes('audio generation'))
        return 'We couldn\'t generate your voiceover. This is usually temporary — please try again.';
    if (msg.includes('Saved lesson not found') || msg.includes('lesson not found'))
        return 'This lesson needs to be saved before generating a voiceover.';
    if (msg.includes('Database not configured') || msg.includes('503') || msg.includes('Service'))
        return 'Saige is temporarily unavailable. Please try again in a moment.';
    if (msg.includes('upload') || msg.includes('storage') || msg.includes('Bucket'))
        return 'There was a problem saving the audio file. Please try again.';

    // Fallback: use backend message if it exists, otherwise generic
    return msg || 'Something went wrong. Please try again.';
};

const VoiceoverModal = ({
    isOpen,
    onClose,
    teacherId,
    lessonDocumentId,
    lessonTitle,
    existingAsset,    // Pre-fetched asset if voiceover already exists
}) => {
    // Steps: 'script' | 'generating' | 'ready'
    const [step, setStep] = useState('script');
    const [scriptText, setScriptText] = useState('');
    const [loadingScript, setLoadingScript] = useState(false);
    const [scriptPhase, setScriptPhase] = useState(0);
    const [audioUrl, setAudioUrl] = useState(null);
    const [durationMs, setDurationMs] = useState(null);
    const [audioPhase, setAudioPhase] = useState(0);
    const [error, setError] = useState(null);
    const [isExistingAsset, setIsExistingAsset] = useState(false);
    // Track whether teacher has edited the script (for analytics)
    const originalScriptRef = useRef('');

    // Audio player
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);

    // Phase timers
    const scriptTimerRef = useRef(null);
    const audioTimerRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (scriptTimerRef.current) {
            clearInterval(scriptTimerRef.current);
            scriptTimerRef.current = null;
        }
        if (audioTimerRef.current) {
            clearInterval(audioTimerRef.current);
            audioTimerRef.current = null;
        }
    }, []);

    // Initialize from existing asset or generate new script
    useEffect(() => {
        if (!isOpen) return;

        setError(null);

        if (existingAsset) {
            // Existing voiceover — jump to ready state
            setScriptText(existingAsset.script_text || '');
            setAudioUrl(existingAsset.audio_url || '');
            setDurationMs(existingAsset.duration_ms || null);
            setIsExistingAsset(true);
            setStep('ready');
        } else {
            // Generate new script
            setStep('script');
            setScriptText('');
            setAudioUrl(null);
            setIsExistingAsset(false);
            generateScript();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, existingAsset]);

    // Cleanup: pause audio + clear timers when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (audioRef.current) {
                audioRef.current.pause();
                setIsPlaying(false);
            }
            clearTimers();
            setScriptPhase(0);
            setAudioPhase(0);
        }
    }, [isOpen, clearTimers]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    // Audio player event handlers
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setAudioDuration(audio.duration);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [audioUrl]);

    const generateScript = async () => {
        setLoadingScript(true);
        setScriptPhase(0);
        setError(null);

        // Phased labels
        scriptTimerRef.current = setInterval(() => {
            setScriptPhase(prev => Math.min(prev + 1, SCRIPT_PHASES.length - 1));
        }, 2500);

        try {
            const res = await voiceover.generateScript({
                lesson_document_id: lessonDocumentId,
                teacher_id: teacherId,
            });
            const script = res.data?.script || '';
            setScriptText(script);
            originalScriptRef.current = script;
            trackAudioEvent('audio_script_generated', {
                word_count: script.split(/\s+/).length,
                lesson_document_id: lessonDocumentId,
                quality_lane: res.data?.quality_lane,
                estimated_duration_seconds: res.data?.estimated_duration_seconds,
            });
        } catch (err) {
            console.error('Script generation failed:', err);
            setError(friendlyError(err));
        } finally {
            clearTimers();
            setLoadingScript(false);
        }
    };

    const handleGenerateAudio = async () => {
        if (!scriptText.trim()) return;



        setStep('generating');
        setAudioPhase(0);
        setError(null);

        trackAudioEvent('audio_generation_started', {
            lesson_document_id: lessonDocumentId,
            word_count: scriptText.split(/\s+/).length,
            script_edited: scriptText !== originalScriptRef.current,
        });

        // Phased labels
        audioTimerRef.current = setInterval(() => {
            setAudioPhase(prev => Math.min(prev + 1, AUDIO_PHASES.length - 1));
        }, 5000);

        try {
            const res = await voiceover.generateAudio({
                lesson_document_id: lessonDocumentId,
                teacher_id: teacherId,
                script_text: scriptText.trim(),
            });

            if (res.data?.success) {
                clearTimers();
                setAudioUrl(res.data.audio_url);
                setDurationMs(res.data.duration_ms);
                setIsExistingAsset(false);
                setStep('ready');
                trackAudioEvent('audio_generation_completed', {
                    lesson_document_id: lessonDocumentId,
                    duration_ms: res.data.duration_ms,
                    quality_lane: res.data.quality_lane,
                });
            } else {
                throw new Error('Audio generation returned no data');
            }
        } catch (err) {
            console.error('Audio generation failed:', err);
            clearTimers();
            setError(friendlyError(err));
            setStep('script'); // Go back to script step on error
            trackAudioEvent('audio_generation_failed', {
                lesson_document_id: lessonDocumentId,
                error: err?.message || String(err),
            });
        }
    };

    const togglePlayback = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play();
            setIsPlaying(true);
            trackAudioEvent('audio_played', { lesson_document_id: lessonDocumentId });
        }
    };

    const handleDownload = () => {
        if (!audioUrl) return;
        trackAudioEvent('audio_downloaded', { lesson_document_id: lessonDocumentId });
        const filename = `${(lessonTitle || 'lesson').replace(/\s+/g, '_')}_voiceover.mp3`;
        // Use fetch→blob to force a real file download instead of opening in a new tab
        fetch(audioUrl)
            .then(res => res.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                // Fallback: open in new tab if blob download fails
                window.open(audioUrl, '_blank');
            });
    };

    const handleRegenerate = () => {
        // Pause any playing audio
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setStep('script');
        setAudioUrl(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setIsExistingAsset(false);
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const formatDuration = (ms) => {
        if (!ms) return null;
        const totalSec = Math.round(ms / 1000);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        if (m === 0) return `${s} sec`;
        if (s === 0) return `${m} min`;
        return `${m} min ${s} sec`;
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Volume2 size={20} className="text-amber-500" />
                            <h2 className="text-lg font-semibold text-[#1d1d1f]">
                                {step === 'ready'
                                    ? (isExistingAsset ? 'Your Saved Voiceover' : 'Voiceover Ready')
                                    : 'Generate Voiceover'
                                }
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6">
                        {/* Error */}
                        {error && (
                            <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <span>{error}</span>
                                    {step === 'script' && (
                                        <button
                                            onClick={() => { setError(null); generateScript(); }}
                                            className="ml-2 font-medium underline underline-offset-2 hover:text-red-700"
                                        >
                                            Try again
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* STEP: Script Review */}
                        {step === 'script' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-slate-600">
                                        Review and edit the narration script before generating audio.
                                    </p>
                                    <button
                                        onClick={generateScript}
                                        disabled={loadingScript}
                                        className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                                    >
                                        <RotateCcw size={12} />
                                        Regenerate
                                    </button>
                                </div>

                                {loadingScript ? (
                                    <div className="flex flex-col items-center py-12 text-slate-500">
                                        <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                                            <Edit3 size={20} className="text-amber-500 animate-pulse" />
                                        </div>
                                        <p className="font-medium text-sm text-[#1d1d1f]">
                                            {SCRIPT_PHASES[scriptPhase]}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            This usually takes a few seconds.
                                        </p>
                                    </div>
                                ) : (
                                    <textarea
                                        value={scriptText}
                                        onChange={(e) => setScriptText(e.target.value)}
                                        className="w-full px-4 py-3 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none leading-relaxed"
                                        rows={12}
                                        placeholder="Narration script will appear here…"
                                    />
                                )}

                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-400">
                                        {scriptText ? `${scriptText.split(/\s+/).length} words · ~${Math.ceil(scriptText.split(/\s+/).length / 150)} min` : ''}
                                    </span>
                                    <button
                                        onClick={handleGenerateAudio}
                                        disabled={!scriptText.trim() || loadingScript}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-[#FFC107] text-black rounded-xl font-medium text-sm hover:bg-[#e6ad00] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Mic size={16} />
                                        Generate Audio
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* STEP: Generating — Phased Progress */}
                        {step === 'generating' && (
                            <div className="flex flex-col items-center py-14 text-slate-500">
                                <div className="relative mb-4">
                                    <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                                        <Volume2 size={28} className="text-amber-500 animate-pulse" />
                                    </div>
                                </div>
                                <p className="font-medium text-[#1d1d1f] text-center">
                                    {AUDIO_PHASES[audioPhase]}
                                </p>
                                <p className="text-sm text-slate-400 mt-2 text-center">
                                    This may take 30–60 seconds depending on script length.
                                </p>
                                <div className="flex gap-1.5 mt-4">
                                    {AUDIO_PHASES.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`w-2 h-2 rounded-full transition-all duration-500 ${
                                                i <= audioPhase ? 'bg-amber-500' : 'bg-slate-200'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* STEP: Ready */}
                        {step === 'ready' && (
                            <div className="space-y-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-green-50 rounded-xl p-4 text-center"
                                >
                                    <Check size={28} className="text-green-500 mx-auto mb-1" />
                                    <p className="font-medium text-green-700 text-sm">
                                        {isExistingAsset
                                            ? 'Your saved voiceover'
                                            : 'Your voiceover is ready! 🎉'
                                        }
                                    </p>
                                    {durationMs && (
                                        <p className="text-xs text-green-500 mt-0.5">
                                            {formatDuration(durationMs)}
                                        </p>
                                    )}
                                </motion.div>

                                {/* Audio Player */}
                                {audioUrl && (
                                    <div className="bg-slate-50 rounded-xl p-4">
                                        <audio ref={audioRef} src={audioUrl} preload="metadata" />

                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={togglePlayback}
                                                className="w-10 h-10 flex items-center justify-center bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors flex-shrink-0"
                                            >
                                                {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                                            </button>

                                            <div className="flex-1 min-w-0">
                                                {/* Progress bar */}
                                                <div
                                                    className="w-full h-1.5 bg-slate-200 rounded-full cursor-pointer"
                                                    onClick={(e) => {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const pct = (e.clientX - rect.left) / rect.width;
                                                        if (audioRef.current) {
                                                            audioRef.current.currentTime = pct * audioDuration;
                                                        }
                                                    }}
                                                >
                                                    <div
                                                        className="h-full bg-amber-500 rounded-full transition-all"
                                                        style={{ width: `${audioDuration ? (currentTime / audioDuration) * 100 : 0}%` }}
                                                    />
                                                </div>
                                                <div className="flex justify-between mt-1">
                                                    <span className="text-xs text-slate-400">{formatTime(currentTime)}</span>
                                                    <span className="text-xs text-slate-400">{formatTime(audioDuration)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleDownload}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors"
                                    >
                                        <Download size={16} />
                                        Download MP3
                                    </button>
                                    <button
                                        onClick={handleRegenerate}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors"
                                    >
                                        <RefreshCw size={16} />
                                        Make New Audio
                                    </button>
                                </div>

                                {/* Regenerate helper text */}
                                <p className="text-xs text-slate-400 text-center -mt-2">
                                    Edit your script and create a fresh recording.
                                </p>

                                {/* Script Toggle */}
                                <details className="group">
                                    <summary className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                                        <Edit3 size={12} />
                                        View/Edit Script
                                    </summary>
                                    <textarea
                                        value={scriptText}
                                        onChange={(e) => setScriptText(e.target.value)}
                                        className="mt-2 w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                                        rows={6}
                                    />
                                </details>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default VoiceoverModal;
